import { useState } from 'react';

import type { Plan } from '@domain/plan/Plan';
import { togglePlanActive } from '@application/plan/managePlan';
import { apiClient } from '@infrastructure/api/client';
import { errorText } from '@infrastructure/api/errors';
import { HttpPlanRepository } from '@infrastructure/api/HttpPlanRepository';
import { DataTable, type DataTableColumn } from '@app/components/data-table/DataTable';
import { PlanFormModal } from '@app/components/PlanFormModal';

const repo = new HttpPlanRepository(apiClient);

export function PlansPage() {
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Plan | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const onToggle = async (plan: Plan) => {
    if (
      !window.confirm(
        plan.is_active
          ? `Deactivate "${plan.name}"? Existing customers keep the plan; new signups can't pick it.`
          : `Reactivate "${plan.name}"?`,
      )
    ) return;
    try {
      await togglePlanActive(repo)(plan);
      setReloadKey((k) => k + 1);
    } catch (e) {
      alert(`Toggle failed: ${errorText(e)}`);
    }
  };

  const columns: DataTableColumn<Plan>[] = [
    { id: 'name', header: 'Plan', accessor: 'name' },
    { id: 'plan_type', header: 'Type', accessor: 'plan_type' },
    { id: 'base_rate', header: 'Rate / SMS', accessor: (r) => `$${r.base_rate}` },
    { id: 'monthly_fee', header: 'Monthly Fee', accessor: (r) => `$${r.monthly_fee}` },
    { id: 'included_messages', header: 'Free texts (once)', accessor: 'included_messages' },
    { id: 'customer_count', header: 'Customers', accessor: (r) => r.customer_count ?? '—' },
    {
      id: 'is_active', header: 'Status',
      accessor: (r) => (
        <span className={r.is_active ? 'text-green-700' : 'text-slate-500'}>
          {r.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      id: 'actions', header: 'Actions',
      accessor: (r) => (
        <span className="space-x-2">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setEditing(r); }}
            className="rounded border px-2 py-0.5 text-xs"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggle(r); }}
            className="rounded border px-2 py-0.5 text-xs"
          >
            {r.is_active ? 'Deactivate' : 'Reactivate'}
          </button>
        </span>
      ),
    },
  ];

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <h2 className="text-lg font-semibold">Plans</h2>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
        >
          + Create plan
        </button>
      </div>
      <DataTable<Plan>
        key={reloadKey}
        columns={columns}
        fetcher={(p) => repo.list(p)}
        pagination="offset"
        defaultPageSize={25}
        rowKey={(r) => r.id}
        emptyState={
          <span data-testid="datatable-empty">
            No plans configured yet — create one to enable signups.
          </span>
        }
      />

      {showCreate && (
        <PlanFormModal
          open
          onClose={() => setShowCreate(false)}
          onSaved={() => setReloadKey((k) => k + 1)}
        />
      )}
      {/* Mounted per plan: the form's fields are seeded from `initial` once */}
      {editing && (
        <PlanFormModal
          key={editing.id}
          open
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => setReloadKey((k) => k + 1)}
        />
      )}
    </section>
  );
}
